import { z } from 'zod';

export const storageModeSchema = z.enum([
    'ALL_AUTHENTICATED_NON_GUESTS',
    'GROUPS_ONLY',
    'DEFAULT_INTERNAL',
    'ALL_USERS',
    'RESTRICTED',
    // Nur als effektive Sichtbarkeit: durch Vererbung sieht niemand (leere
    // Schnittmenge). Wird nie direkt gesetzt.
    'NONE',
]);

export const storageUserSchema = z.object({
    id: z.number(),
    displayName: z.string(),
});

const folderTreeNodeBaseSchema = z.object({
    id: z.number(),
    name: z.string(),
    description: z.string().nullable().optional(),
    parent_id: z.number().nullable().optional(),
    can_view: z.boolean().optional().default(true),
    can_upload: z.boolean().optional(),
    can_manage: z.boolean().optional(),
    created_at: z.string(),
    updated_at: z.string().nullable().optional(),
    created_by: storageUserSchema,
    updated_by: storageUserSchema.nullable().optional(),
    child_count: z.number().int().nonnegative().optional(),
    file_count: z.number().int().nonnegative().optional(),
    is_favorite: z.boolean().optional().default(false),
    visible_groups: z.array(z.number().int()).optional(),
    visibility_groups: z.array(z.number().int()).optional(),
    visibility_mode: storageModeSchema.optional(),
    // Effektive Sichtbarkeit nach Vererbung (vom Backend berechnet).
    effective_visibility_mode: storageModeSchema.optional(),
    effective_visibility_group_ids: z.array(z.number().int()).optional(),
    restricted_by_ancestor: z.boolean().optional(),
    include_guests: z.boolean().optional(),
    upload_any_authenticated: z.boolean().optional(),
    upload_mode: storageModeSchema.optional(),
    upload_include_guests: z.boolean().optional(),
    upload_groups: z.array(z.number().int()).optional(),
});

type FolderTreeNode = z.infer<typeof folderTreeNodeBaseSchema> & {
    children: FolderTreeNode[];
};

type FolderTreeNodeInput = z.input<typeof folderTreeNodeBaseSchema> & {
    children?: FolderTreeNodeInput[];
};

export const folderTreeNodeSchema: z.ZodType<FolderTreeNode, z.ZodTypeDef, FolderTreeNodeInput> = z.lazy(() =>
    folderTreeNodeBaseSchema.extend({
        children: z.array(folderTreeNodeSchema).default([]),
    })
);

export type IFolderTreeNode = FolderTreeNode;

export const folderTreeSchema = z.array(folderTreeNodeSchema);

export const fileItemSchema = z.object({
    id: z.number(),
    original_name: z.string(),
    size: z.number().nonnegative(),
    mime_type: z.string(),
    created_at: z.string(),
    uploaded_by: storageUserSchema,
    is_favorite: z.boolean().optional().default(false),
    can_delete: z.boolean().optional(),
    can_rename: z.boolean().optional(),
    download_url: z.string().optional(),
});

export const folderFilesSchema = z.array(fileItemSchema);

export type IFileItem = z.infer<typeof fileItemSchema>;
export const storageGroupSchema = z.object({
    id: z.number(),
    customGroup: z.string().optional(),
    name: z.string().optional(),
    title: z.string().optional(),
});

export const storageGroupsSchema = z.array(storageGroupSchema);
export type IStorageGroup = z.infer<typeof storageGroupSchema>;

export const storageNamedGroupSchema = z.object({
    idCustomGroup: z.number(),
    name: z.string(),
});

export const storageNamedGroupsSchema = z.array(storageNamedGroupSchema);
export type IStorageNamedGroup = z.infer<typeof storageNamedGroupSchema>;

export const storageGroupFallbacks: IStorageGroup[] = [
    { id: 1, customGroup: 'Presid' },
    { id: 2, customGroup: 'AH Vorstand' },
    { id: 3, customGroup: 'Exec Team' },
    { id: 4, customGroup: 'Gäste' },
];

export const folderUpsertSchema = z
    .object({
        name: z.string().trim().min(1, 'Name ist erforderlich'),
        description: z.string().trim().optional(),
        visibility_mode: storageModeSchema.default('DEFAULT_INTERNAL'),
        include_guests: z.boolean().default(false),
        visible_groups: z.array(z.number().int()).default([]),
        visibility_groups: z.array(z.number().int()).default([]),
        upload_mode: storageModeSchema.default('DEFAULT_INTERNAL'),
        upload_include_guests: z.boolean().default(false),
        upload_any_authenticated: z.boolean().optional(),
        upload_groups: z.array(z.number().int()).default([]),
        parent_id: z.number().nullable().optional(),
    })
    .superRefine((value, ctx) => {
        const effectiveVisibilityGroups =
            value.visibility_groups.length > 0 ? value.visibility_groups : value.visible_groups;
        const isVisibilityRestricted = value.visibility_mode === 'GROUPS_ONLY' || value.visibility_mode === 'RESTRICTED';
        const isUploadRestricted = value.upload_mode === 'GROUPS_ONLY' || value.upload_mode === 'RESTRICTED';

        if (isVisibilityRestricted && effectiveVisibilityGroups.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['visible_groups'],
                message: 'Mindestens eine sichtbare Gruppe wählen',
            });
        }
        if (isUploadRestricted && value.upload_groups.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['upload_groups'],
                message: 'Mindestens eine Upload-Gruppe wählen',
            });
        }
        if (isVisibilityRestricted && effectiveVisibilityGroups.some((groupId) => ![1, 2, 3].includes(groupId))) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['visible_groups'],
                message: 'Nur interne Gruppen (1-3) sind für eingeschränkte Sichtbarkeit erlaubt',
            });
        }
        if (isUploadRestricted && value.upload_groups.some((groupId) => ![1, 2, 3].includes(groupId))) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['upload_groups'],
                message: 'Nur interne Gruppen (1-3) sind für eingeschränkten Upload erlaubt',
            });
        }
    });

export type IFolderUpsertInput = z.infer<typeof folderUpsertSchema>;
